/**
 * AdminTopPill — widget-local top bar for /admin/*.
 *
 * Pins the floating-capsule treatment from the design artboard
 * (`.design-incoming/admin-20260522/source/admin.jsx` AdminTopPill):
 * a sticky pill (`top-4`, `--shadow-md`, `rounded-full`, 56 px tall)
 * centered over the canvas — the same skeleton as the PMS dashboard
 * `TopPill`, with the Admin-specific differences below.
 *
 * Regions, left → right:
 *   brand (BrandMark + "Desata" + uppercase "Admin" portal badge)
 *   · divider · global-search button (+ ⌘K hint) · spacer
 *   · notifications bell · account avatar (→ the shared AccountMenu).
 *
 * **Admin-specific vs the PMS TopPill:**
 *   1. **"Admin" portal badge, not an org chip.** The artboard labels
 *      the platform-admin context with a small brand-tinted "ADMIN"
 *      badge beside the wordmark — there is no clickable org chip and
 *      no org switcher (Admin operates platform-wide, not per
 *      customer-org, per `NAVIGATION_SPEC.md §2 "Don't"`). "Admin"
 *      names the *portal* the entitlement gate enforces — unlike a
 *      "Platform admin" *role* badge, which would over-claim (all five
 *      PlatformRole values reach the Admin portal per DATA_MODEL.md §3).
 *   2. **Search placeholder** is Admin-scoped ("Search companies,
 *      staff, imports…").
 *
 * **Phase-1 honesty (no fake backends):**
 *   - The search button is a visual affordance + ⌘K shortcut hint; it
 *     does not fabricate results. Global Admin search wires when the
 *     backend ships (mirrors the PMS search no-op-stub precedent).
 *   - The bell renders no unread dot: there is no notifications source
 *     yet, and an "unknown" count must never read as a real one. The
 *     dot returns when a count source lands (PMS SCRUM-62 precedent).
 *
 * **Portal switcher (SCRUM-114):** lives inside the shared `AccountMenu` as a
 * "Switch portal" section for multi-portal users — a conscious divergence
 * from the artboard's no-toggle default (see the menu + decision doc). The
 * old invisible pill `data-portal-switcher-slot` seam is removed. Mobile
 * collapse (hamburger + drawer) stays deferred per `NAVIGATION_SPEC.md §5`.
 *
 * **Preferences:** the TopPill (always mounted in the shell's
 * authenticated+entitled branch) owns the `useUserPreferences()` read,
 * applies the theme via `useApplyThemePreference`, and passes the
 * theme/density + setters into the menu's Density/Appearance controls.
 *
 * **Second-consumer note:** the PMS dashboard `TopPill` + `AccountMenu`
 * and this admin pair are two consumers of the same pattern — the
 * kit-promotion trigger. Promotion of a shared TopPill / AccountMenu /
 * SegmentedControl to the kit is tracked as a follow-up so this pass stays
 * bounded and doesn't churn the shipped PMS surface.
 */

import * as React from 'react';
import { Link } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Avatar, BrandMark, Icon, IconBtn } from '@sparta/ui-kit';
import { useMe } from '../../../shared/auth/use-me.js';
import { apiClient } from '../../../shared/api-client.js';
import { queryClient } from '../../../shared/query-client.js';
import { navigateToLogin } from '../navigate.js';
import {
  AccountMenu,
  type AccountMenuActionLink,
} from '../../../entities/account/AccountMenu.js';
import { useUserPreferences } from '../../../shared/hooks/useUserPreferences.js';
import { useApplyThemePreference } from '../../../shared/hooks/useApplyThemePreference.js';

/** Admin-portal account-menu action links (placeholder routes under /admin). */
const ADMIN_ACTION_LINKS: readonly AccountMenuActionLink[] = [
  { label: 'Account settings', icon: 'user', to: '/admin/account' },
  { label: 'Notifications', icon: 'bell', to: '/admin/notifications' },
  { label: 'Help & support', icon: 'info', to: '/admin/help' },
];

export interface AdminTopPillProps {
  /** Compact (mobile) layout — show the hamburger, hide the search field. */
  readonly compact?: boolean;
  /** Open the mobile nav drawer (compact only). */
  readonly onMenu?: () => void;
}

export function AdminTopPill({
  compact = false,
  onMenu,
}: AdminTopPillProps = {}): React.ReactElement {
  const me = useMe();
  // The account menu hosts the Density/Appearance controls; the TopPill (always
  // mounted in the shell) owns the preferences read + applies the theme so
  // every /admin page honors it, even before the menu is opened.
  const { preferences, setTheme, setDensity } = useUserPreferences();
  useApplyThemePreference(preferences.theme);
  const [accountMenuOpen, setAccountMenuOpen] = React.useState(false);
  // SCRUM-15 reviewer fix #4: ref to the trigger button so the
  // <AccountMenu/> outside-click handler can exclude the trigger from
  // its close-on-mousedown set (avoids the close-then-reopen race
  // between mousedown and click on the trigger itself).
  const accountMenuTriggerRef = React.useRef<HTMLButtonElement>(null);

  // Escape closes the account menu (the menu itself owns outside-click).
  React.useEffect(() => {
    if (!accountMenuOpen) return undefined;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setAccountMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [accountMenuOpen]);

  /**
   * Sign-out — same deliberate order as the dashboard flow:
   *   1. Best-effort server revoke (POST /auth/logout); a wire failure
   *      must not strand the user, but we report it to Sentry so the
   *      orphaned-session signal isn't silent.
   *   2. queryClient.clear() evicts every cache entry (incl.
   *      ['auth','me']) so a same-tab back-button can't surface stale
   *      user data.
   *   3. Hard navigation to /login resets the React tree to a clean
   *      unauthenticated boot.
   */
  const onSignOut = async (): Promise<void> => {
    try {
      await apiClient.POST('/auth/logout', {});
    } catch (error) {
      Sentry.captureException(error, {
        tags: { surface: 'admin-top-pill-signout', endpoint: 'POST /auth/logout' },
      });
    }
    queryClient.clear();
    navigateToLogin();
  };

  const user = me?.user ?? { id: '', email: '' };

  return (
    <header className="sticky top-4 z-40 flex justify-center px-4">
      {/* `[box-shadow:var(--shadow-md)]` arbitrary-property form: the
          `shadow-[var(...)]` shorthand routes the value to
          `--tw-shadow-color` and emits no box-shadow. Permitted per
          ADR-0003 allow-list. */}
      <nav
        aria-label="Admin top navigation"
        className="flex items-center gap-2 bg-[var(--bg-surface)] border border-solid border-[var(--border-subtle)] [box-shadow:var(--shadow-md)] rounded-full p-1.5 h-14 w-full"
      >
        {/* Mobile: hamburger opens the nav drawer (the left rail is hidden
            on compact). Mirrors the PMS dashboard TopPill. */}
        {compact ? (
          <IconBtn label="Open menu" onClick={onMenu}>
            <Icon name="menu" size={20} aria-hidden />
          </IconBtn>
        ) : null}

        {/* Brand + portal badge */}
        <Link
          to="/admin"
          aria-label="Desata admin home"
          className="flex items-center gap-2.5 pl-2.5 pr-3.5 text-[var(--fg-primary)] no-underline shrink-0"
        >
          <BrandMark size={28} />
          <span className="font-bold text-base tracking-[-0.2px]">Desata</span>
          <span className="text-[10px] font-bold uppercase tracking-[0.6px] px-2 py-0.5 rounded text-[var(--brand-700)] bg-[color-mix(in_oklab,var(--brand-500)_14%,var(--bg-surface))] border border-solid border-[color-mix(in_oklab,var(--brand-500)_30%,transparent)]">
            Admin
          </span>
        </Link>

        {/* Search is hidden on compact (mobile) — the artboard mobile pill
            shows only brand + bell + avatar. */}
        {!compact ? (
          <>
            <span
              aria-hidden="true"
              className="w-px h-6 bg-[var(--border-subtle)] shrink-0"
            />

            {/* Global Admin search — visual affordance + ⌘K hint. Honest
                Phase-1 stub: no fabricated results until the search backend
                ships (PMS search no-op-stub precedent). Hand-rolled
                <button>: styled as a search field, not a kit Button. */}
            {/* eslint-disable-next-line @sparta/no-hand-rolled-button */}
            <button
              type="button"
              aria-label="Search companies, staff, imports"
              aria-keyshortcuts="Meta+K Control+K"
              // SCRUM-79 audit exemption: `min-w-[240px]` is the search-bar
              // minimum from the artboard; no Tailwind default matches
              // (closest `min-w-56`=224 px / `min-w-64`=256 px). Awaiting a
              // branded `minWidth.search-bar` token.
              // eslint-disable-next-line @sparta/no-pixel-literal-arbitrary
              className="flex items-center gap-2 h-10 px-3.5 bg-[var(--bg-sunken)] border-none rounded-full text-[var(--fg-tertiary)] text-[13px] cursor-pointer min-w-[240px]"
            >
              <Icon name="search" size={16} aria-hidden />
              <span>Search companies, staff, imports…</span>
              <span className="ml-auto text-[11px] px-1.5 py-0.5 bg-[var(--bg-surface)] border border-solid border-[var(--border-subtle)] rounded text-[var(--fg-secondary)]">
                ⌘K
              </span>
            </button>
          </>
        ) : null}

        <div className="flex-1" />

        {/* Bell — no unread dot in Phase-1 (no count source; "unknown"
            must not read as a real unread count). */}
        <IconBtn label="Notifications">
          <Icon name="bell" size={18} aria-hidden />
        </IconBtn>

        {/* Account avatar — toggles the account menu. Generic accessible
            name (no name fabricated from email). Hand-rolled <button>:
            an avatar + chevron pill composite, not a kit Button.

            SCRUM-15 reviewer fix #4: `accountMenuTriggerRef` is passed to
            <AccountMenu/> so the menu's outside-click handler excludes
            this trigger from its close-on-mousedown set — otherwise a
            click on the avatar while the menu is open would close-then-
            reopen (mousedown closes, click toggles back). */}
        <div className="relative">
          {/* eslint-disable-next-line @sparta/no-hand-rolled-button */}
          <button
            ref={accountMenuTriggerRef}
            type="button"
            aria-label="Open account menu"
            aria-haspopup="dialog"
            aria-expanded={accountMenuOpen}
            onClick={() => setAccountMenuOpen((v) => !v)}
            className={`flex items-center gap-2 h-10 pl-1 pr-2.5 border-none rounded-full cursor-pointer text-[var(--fg-primary)] ${
              accountMenuOpen ? 'bg-[var(--bg-active)]' : 'bg-[var(--bg-sunken)]'
            }`}
          >
            <Avatar name={user.name} size={32} />
            <Icon name="chevDown" size={14} aria-hidden />
          </button>
          {accountMenuOpen ? (
            <AccountMenu
              user={user}
              theme={preferences.theme}
              density={preferences.density}
              onThemeChange={setTheme}
              onDensityChange={setDensity}
              portals={me?.portals ?? []}
              currentPortal="Admin"
              actionLinks={ADMIN_ACTION_LINKS}
              onClose={() => setAccountMenuOpen(false)}
              onSignOut={onSignOut}
              triggerRef={accountMenuTriggerRef}
            />
          ) : null}
        </div>
      </nav>
    </header>
  );
}
